import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { Loader2, X } from 'lucide-react'
import {
  YardiResidentReviewBody,
  libertyRecord,
  recordName,
} from '@/features/property/components/YardiResidentReviewBody'
import { syncYardiResidents } from '@/features/property/services/propertyYardiResidentService'
import type { YardiResidentMatch, YardiResidentSyncResponse } from '@/features/property/types'
import { showApiError } from '@/lib/utils/notifications'
import { useBodyScrollLock } from '@/hooks/useBodyScrollLock'
import type { MatchingResidentRow } from '../types'

function syncRows(response: YardiResidentSyncResponse): YardiResidentMatch[] {
  return [
    ...(response.matched ?? []),
    ...(response.skipped ?? []),
    ...(response.alreadySynced ?? []),
    ...(response.notMappable ?? []),
  ]
}

function pickReviewMatch(rows: YardiResidentMatch[], residentId: number): YardiResidentMatch | null {
  const matches = rows.filter((row) => {
    const liberty = libertyRecord(row)
    return [row.liberty_entity_id, liberty.resident_id, liberty.resident_default_id].some(
      (value) => Number(value) === residentId,
    )
  })
  return (
    matches.find((row) => String(row.status ?? '').toUpperCase() === 'MATCHED') ??
    matches.find((row) => String(row.status ?? '').toUpperCase() === 'ALREADY_SYNCED') ??
    matches[0] ??
    null
  )
}

function liveLibertyRecord(row: MatchingResidentRow) {
  const name = `${row.first_name || ''} ${row.last_name || ''}`.trim()
  return {
    first_name: row.first_name || undefined,
    last_name: row.last_name || undefined,
    name: name || undefined,
    email: row.email || undefined,
    applicant_id: row.applicant_id || undefined,
  }
}

function liveReviewMatch(row: MatchingResidentRow, syncMatch: YardiResidentMatch | null): YardiResidentMatch {
  const liberty = liveLibertyRecord(row)
  if (!syncMatch) {
    return {
      liberty_entity_id: row.liberty_entity_id,
      source_entity_id: row.matched_tcode || undefined,
      match_reason: row.match_reason || undefined,
      score: row.matching_score ?? undefined,
      status: row.status,
      liberty_matched_candidate: liberty,
      source_matched_candidate: row.matched_tcode ? { tCode: row.matched_tcode } : {},
    }
  }
  const syncLiberty = syncMatch.liberty_matched_candidate ?? syncMatch.liberty_main_candidate ?? {}
  return {
    ...syncMatch,
    liberty_entity_id: syncMatch.liberty_entity_id ?? row.liberty_entity_id,
    source_entity_id: syncMatch.source_entity_id ?? row.matched_tcode ?? undefined,
    match_reason: syncMatch.match_reason ?? row.match_reason ?? undefined,
    liberty_matched_candidate: {
      ...syncLiberty,
      ...liberty,
      first_name: liberty.first_name || syncLiberty.first_name,
      last_name: liberty.last_name || syncLiberty.last_name,
      email: liberty.email || syncLiberty.email,
      applicant_id: liberty.applicant_id ?? syncLiberty.applicant_id,
    },
  }
}

function MatchingResidentReviewDialogBody({
  row,
  onClose,
}: {
  row: MatchingResidentRow
  onClose: () => void
}) {
  const [loading, setLoading] = useState(true)
  const [match, setMatch] = useState<YardiResidentMatch | null>(null)

  useBodyScrollLock(true)

  useEffect(() => {
    let cancelled = false
    void syncYardiResidents({
      property_id: row.liberty_property_id,
      company_id: row.liberty_company_id,
      property_code: row.source_property_id,
      resident_type: row.matching_phase === 'past' ? 'past' : 'current',
      resident_id: row.liberty_entity_id,
      resident_default_id: row.liberty_entity_id,
    })
      .then((response) => {
        if (cancelled) return
        setMatch(pickReviewMatch(syncRows(response), row.liberty_entity_id))
      })
      .catch((error) => {
        if (!cancelled) showApiError(error)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [row])

  const titleName =
    `${row.first_name || ''} ${row.last_name || ''}`.trim() ||
    (match ? recordName(libertyRecord(match)) : `Resident ${row.liberty_entity_id}`)

  return createPortal(
    <div
      className="fixed inset-0 z-[10030] flex items-center justify-center bg-slate-900/40 p-3 md:p-6"
      onClick={onClose}
      data-testid="yardi-matching-review-overlay"
    >
      <aside
        className="relative flex h-full max-h-[calc(100vh-1.5rem)] w-full max-w-[1280px] flex-col overflow-hidden rounded-[20px] border border-[#e4ebf7] bg-white shadow-2xl md:max-h-[calc(100vh-3rem)]"
        onClick={(event) => event.stopPropagation()}
        data-testid="yardi-matching-review-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="yardi-matching-review-title"
      >
        <div className="flex items-start justify-between gap-4 border-b border-[#e4ebf7] px-6 py-5">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.06em] text-[#3151d3]">Detailed Review</p>
            <h3 id="yardi-matching-review-title" className="mt-0.5 text-lg font-bold text-[#16213e]">
              {titleName}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-full border border-[#d5e0f1] bg-white"
            aria-label="Close detailed review"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
          {loading ? (
            <div className="flex min-h-[320px] items-center justify-center text-sm text-slate-500">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Loading Yardi review…
            </div>
          ) : (
            <YardiResidentReviewBody
              row={liveReviewMatch(row, match)}
              showSkipReason={Boolean(match) && String(match?.status ?? '').toUpperCase() === 'SKIPPED' && !match?.score_breakdown}
            />
          )}
        </div>
      </aside>
    </div>,
    document.body,
  )
}

export function MatchingResidentReviewDialog({
  row,
  onClose,
}: {
  row: MatchingResidentRow | null
  onClose: () => void
  onSaved?: () => void
}) {
  if (!row) return null
  return <MatchingResidentReviewDialogBody key={row.match_status_id} row={row} onClose={onClose} />
}
